import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, CheckCircle2, ChevronDown, Circle, CircleDashed, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { ConfBadge } from "@/pages/candidate/profile/parts";
import { SECTIONS } from "./sections";
import { arr } from "./fields";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isBlank = (o) => Object.values(o || {}).every((v) => (Array.isArray(v) ? v.length === 0 : typeof v === "object" ? !v : !String(v ?? "").trim()));
const bullets = (v) => arr(v).map((s) => String(s).trim()).filter(Boolean);

export const cleanProfile = (p) => ({
  ...p,
  experience: arr(p.experience).map((e) => ({ ...e, responsibilities: bullets(e.responsibilities), achievements: bullets(e.achievements), highlights: bullets(e.highlights) })).filter((e) => !isBlank(e)),
  education: arr(p.education).filter((e) => !isBlank(e)),
  projects: arr(p.projects).filter((e) => !isBlank(e)),
  certifications: arr(p.certifications).filter((e) => typeof e === "string" || !isBlank(e)),
});

function StatusPill({ done, required }) {
  if (done) return <span className="hidden sm:inline-flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-500 dark:text-emerald-300"><Check className="h-3 w-3" />Looks good</span>;
  return <span className={cn("hidden sm:inline rounded-full border px-2 py-0.5 text-[11px] font-medium", required ? "border-amber-400/40 text-amber-600 dark:text-amber-300" : "border-border text-muted-foreground")}>{required ? "To review" : "Optional"}</span>;
}

function SectionCard({ s, p, open, done, required, gated, nextTitle, canConfirm, onToggle, onConfirm, children }) {
  const Icon = s.icon;
  return (
    <section id={`review-${s.k}`} className={cn("scroll-mt-24 rounded-2xl border bg-card/70 transition-[border-color,box-shadow] duration-300", open ? "border-primary/40 shadow-xl shadow-black/10" : "border-border")} data-testid={`review-section-${s.k}`}>
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center gap-3 px-4 py-4 text-left sm:px-5" data-testid={`review-open-${s.k}`}>
        <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-colors", done ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-500 dark:text-emerald-300" : "border-border bg-muted/40 text-primary")}>
          {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-display font-semibold">{s.title}</span>
          <span className="block truncate text-xs text-muted-foreground" data-testid={`review-summary-${s.k}`}>{s.summary(p) || "Nothing found in your CV. Add it if you like."}</span>
        </span>
        {!done && required && <ConfBadge v={p.confidence?.[s.conf]} />}
        {gated && <StatusPill done={done} required={required} />}
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300", open && "rotate-180")} />
      </button>
      {open && (
        <div className="space-y-5 border-t border-border px-4 py-5 sm:px-5 reveal-up">
          {children}
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border/60 pt-4">
            {gated ? (
              <Button type="button" onClick={onConfirm} disabled={!canConfirm} className="gap-1.5" data-testid={`confirm-section-${s.k}`}>
                <Check className="h-4 w-4" />{nextTitle ? `Looks good, next: ${nextTitle}` : "Looks good"}
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={onToggle} className="gap-1.5" data-testid={`close-section-${s.k}`}><Check className="h-4 w-4" />Done</Button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function ReviewRail({ required, done, open, go, gated }) {
  const reviewed = required.filter((k) => done[k]).length;
  return (
    <nav className="sticky top-24 space-y-1" aria-label="Profile sections" data-testid="review-rail">
      {gated && (
        <div className="mb-4 px-3">
          <div className="mb-2 text-xs text-muted-foreground">{reviewed} of {required.length} sections reviewed</div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${(reviewed / Math.max(1, required.length)) * 100}%` }} /></div>
        </div>
      )}
      {SECTIONS.map((s) => {
        const Status = done[s.k] ? CheckCircle2 : required.includes(s.k) ? Circle : CircleDashed;
        return (
          <button key={s.k} type="button" onClick={() => go(s.k)} data-testid={`review-rail-${s.k}`}
            className={cn("flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors", open === s.k ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground")}>
            <Status className={cn("h-4 w-4 shrink-0", done[s.k] ? "text-emerald-500" : "text-muted-foreground/70")} />{s.title}
          </button>
        );
      })}
    </nav>
  );
}

function ReviewFooter({ gated, reviewed, total, pending, emailOk, go, extraActions, onCancel, cancelLabel, cancelTestId, onSubmit, canSubmit, busy, submitLabel, submitTestId, stickyClass }) {
  const nextTitle = SECTIONS.find((s) => s.k === pending[0])?.title;
  return (
    <div id="review-footer" className={cn("sticky z-30 mt-6 flex flex-wrap items-center gap-2 rounded-2xl border border-border/70 bg-card/90 px-3 py-2.5 shadow-xl backdrop-blur-md sm:gap-3 sm:px-4", stickyClass)} data-testid="review-footer">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {gated ? (
          <>
            <span className="shrink-0 font-mono text-xs text-primary" data-testid="review-progress">{reviewed}/{total} reviewed</span>
            {pending.length > 0 ? <button type="button" onClick={() => go(pending[0])} className="truncate text-xs text-amber-600 hover:underline dark:text-amber-300" data-testid="review-gate-hint">Next up: {nextTitle}</button>
              : !emailOk ? <button type="button" onClick={() => go("about")} className="truncate text-xs text-rose-400 hover:underline" data-testid="review-gate-hint">Add your email in About you</button>
              : <span className="text-xs text-emerald-500 dark:text-emerald-300">All set</span>}
          </>
        ) : <span className="truncate text-xs text-muted-foreground">Nothing is saved until you press {submitLabel}.</span>}
      </div>
      {extraActions}
      {onCancel && <Button type="button" variant="ghost" onClick={onCancel} data-testid={cancelTestId}>{cancelLabel}</Button>}
      <Button type="button" onClick={onSubmit} disabled={!canSubmit} className="gap-1.5" data-testid={submitTestId}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{submitLabel}<ArrowRight className="h-4 w-4" /></>}
      </Button>
    </div>
  );
}

export function CvReviewEditor({ profile, setProfile, gated = true, requireEmail = false, submitLabel, submitTestId = "review-submit", busy, onSubmit, onCancel, cancelLabel = "Cancel", cancelTestId = "review-cancel", extraActions, stickyClass = "bottom-4" }) {
  const [required] = useState(() => SECTIONS.filter((s) => s.always || arr(profile[s.list]).length).map((s) => s.k));
  const [done, setDone] = useState({});
  const [open, setOpen] = useState(() => (gated ? required[0] : null));
  const emailOk = !requireEmail || EMAIL_RE.test((profile.contact?.email || "").trim());
  const pending = gated ? required.filter((k) => !done[k]) : [];
  const patch = (k) => (fn) => { setProfile((p) => ({ ...p, ...fn(p) })); setDone((d) => (d[k] ? { ...d, [k]: false } : d)); };
  const scrollTo = (id, block = "start") => setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block }), 80);
  const go = (k) => { setOpen(k); scrollTo(`review-${k}`); };
  const confirm = (k) => {
    const nd = { ...done, [k]: true };
    setDone(nd);
    const next = required.find((x) => !nd[x]);
    if (next) go(next); else { setOpen(null); scrollTo("review-footer", "end"); }
  };
  const nextAfter = (k) => SECTIONS.find((s) => s.k === required.find((x) => x !== k && !done[x]))?.title;
  return (
    <div data-testid="cv-review-editor">
      <div className="grid gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="hidden lg:block"><ReviewRail required={required} done={done} open={open} go={go} gated={gated} /></aside>
        <div className="min-w-0 space-y-3">
          {SECTIONS.map((s) => (
            <SectionCard key={s.k} s={s} p={profile} open={open === s.k} done={!!done[s.k]} required={required.includes(s.k)} gated={gated}
              nextTitle={nextAfter(s.k)} canConfirm={s.k !== "about" || emailOk} onToggle={() => setOpen(open === s.k ? null : s.k)} onConfirm={() => confirm(s.k)}>
              <s.Editor p={profile} patch={patch(s.k)} requireEmail={requireEmail} emailOk={emailOk} />
            </SectionCard>
          ))}
        </div>
      </div>
      <ReviewFooter gated={gated} reviewed={required.length - pending.length} total={required.length} pending={pending} emailOk={emailOk} go={go}
        extraActions={extraActions} onCancel={onCancel} cancelLabel={cancelLabel} cancelTestId={cancelTestId} submitLabel={submitLabel} submitTestId={submitTestId}
        busy={busy} canSubmit={!busy && pending.length === 0 && emailOk} onSubmit={() => onSubmit(cleanProfile(profile))} stickyClass={stickyClass} />
    </div>
  );
}
